// Копирайт натальной карты — spec/08_copy.md, раздел «Натальная карта».
//
// ГРАНИЦА ГОЛОСА (docs/domain.md §8). Луня ведёт интерфейс: заголовки, кнопки, экран расчёта,
// подсказки под полями — это её реплики. САМ РАЗБОР голосом не пишется: он приходит с бэкенда
// из справочника трактовок (`apps/api/src/astro/reading`) и говорит по делу.
//
// Отклонения от 08_copy.md — по правке основателя «текст должен соответствовать тому, что
// реально посчитано»:
//   • `calc_note` обещал «десять планет»: считаются семь (`astro/ephemeris.ts`), а дома —
//     только при известных времени И городе;
//   • подписи китов путали Солнце с асцендентом («кто ты для мира» — это про асцендент);
//   • `no_time` называл причиной одно время: без города асцендент тоже не считается;
//   • «семь светил» — неверный термин: светил ровно два, Солнце и Луна, остальные пять
//     планеты. Где нужно число, состав называется поимённо (docs/domain.md §8, словарь).
import { READING_DISCLAIMER, textByGender } from '@lunya/contracts'

import type { NatalCopy } from './types'

export const natal: NatalCopy = {
  intro_title: 'Небо в ту самую минуту',
  // «Давай минуту и место» читалось как предложение, а не как просьба заполнить поля
  // (правка редактора): на экране с двумя полями нужен глагол действия.
  data_title: 'Укажи минуту и место',
  time_note: 'без него нет асцендента и домов',
  city_note: 'нужен для часового пояса и координат',
  calc: 'Считаю твоё небо',
  calc_note: 'Два светила, пять планет, один спаниель с калькулятором',
  sun_sub: 'характер и мотивы',
  moon_sub: 'эмоции и отдых',
  asc_sub: ({ gender }) =>
    textByGender(gender, 'как{им|ой} тебя видят сначала', 'что видят первым'),
  insight: 'ЧТО Я ТУТ ВИЖУ',
  houses_link: 'Разбор по всем 12 домам',
  // Экран глубокого разбора: точки и углы. Луня ведёт интерфейс — заголовок, подсказку,
  // подписи разделов, — а сам разбор внутри написан по делу и без её голоса (правило 21).
  deep_link: 'Точки и углы карты',
  deep_title: 'Точки и углы',
  deep_intro:
    'Тут карта разобрана по частям: сначала на чём она стоит, потом каждая точка отдельно, потом углы между ними. Углы отсортированы от самого точного к самому широкому — чем точнее, тем заметнее.',
  deep_foundation: 'НА ЧЁМ КАРТА СТОИТ',
  deep_points: 'КАЖДАЯ ТОЧКА',
  deep_aspects: 'УГЛЫ МЕЖДУ ТОЧКАМИ',
  deep_no_aspects:
    'Углов в карте не нашлось: без времени рождения считаются только положения в знаках.',
  deep_applying: 'сходится',
  houses_intro:
    'Дом — это сфера жизни, всего их двенадцать. Планета в доме показывает, куда уходит больше внимания. Считаю два светила и пять планет — семь на двенадцать домов, поэтому пустых всегда минимум пять. Это норма, а не пробел.',
  share: 'Поделиться картой',
  no_time: 'Асцендент не посчитать: нужны и время, и город рождения.',
  other_cta: 'Построить карту другому человеку',
  other_title: 'Кому считаем?',
  other_privacy: 'Чужие данные я никуда не отдаю — посчитаю и забуду.',
  other_again: 'Построить ещё одному',
  // Дисклеймер о характере разбора. Раньше он приезжал последним абзацем сводки, а сводку
  // с экрана убрали — вместе с ней пропал и он. Возвращён строкой-ссылкой под разбором
  // (правка основателя): плашкой в тексте он занимал место каждый раз, а прочитывался один.
  disclaimer_link: 'Не является рекомендацией',
  disclaimer_title: 'Что это за разбор',
  // Юридически значимая формулировка живёт в контрактах и одна на бэкенд и фронт
  // (`contracts/domain/disclaimer.ts`) — тут она только подставляется, а не переписывается.
  disclaimer_legal: READING_DISCLAIMER,
  // Дальше — два абзаца с разделением, которое держит весь продукт (docs/domain.md §8):
  // расчёт настоящий и проверяемый, трактовка настоящей быть не может. Раньше тут стояла
  // общая фраза «расчёт настоящий»; основатель попросил назвать метод и школу поимённо —
  // проверяемое утверждение вместо обещания. Всё названное здесь обязано совпадать с кодом:
  // ряды — `astro/ephemeris.ts`, дома — `astro/houses.ts`.
  disclaimer_calc_title: 'КАК СЧИТАЕМ',
  disclaimer_calc:
    'Положения настоящие, а не выдуманные под текст. Солнце, Луну и восемь планет считаем по астрономическим рядам VSOP87 и ELP-2000, Плутон — по теории Мееуса. Долготы видимые геоцентрические на дату рождения, зодиак тропический. Дома — по Плацидусу, а за полярным кругом, где Плацидус не определён, по Порфирию. Любую цифру можно сверить с любым астрологическим сервисом: расхождение выходит в доли угловой минуты.',
  disclaimer_school_title: 'ОТКУДА ТРАКТОВКИ',
  disclaimer_school:
    'Тексты написаны по современной психологической школе — той, что читает карту как описание характера и склонностей, а не как предсказание событий. Поэтому в разборе нет ни прогнозов, ни дат, ни диагнозов, а каждое утверждение опирается на конкретную часть расчёта — знак, дом или угол между точками. Но школа остаётся школой: это способ читать, а не научный факт, и решать по нему ничего не стоит.',
  disclaimer_cta: 'Понятно',
  // Хвост «подставлю всё, что знаю» снят (правка редактора): ряд кружков и так показывает,
  // что данные подставятся, а строка над ним должна только называть выбор.
  subject_strip: 'Кому считаем',
  subject_self: 'Себе',
  subject_manual: 'вручную',
  // Зеркало `compat.step2_from_link`: чужие время и город в форме обязаны называть источник.
  subject_from_link: ({ name }) => `Время и город — из вашей узы: так они записаны у ${name}`,
  stats_cta: 'Посмотреть мою натальную карту',
}
